<template>
  <div class="planning-page">
    <div class="planning-toolbar">
      <div>
        <p class="eyebrow">Gestion RH</p>
        <h1>Planning RH</h1>
      </div>
      <div class="toolbar-actions">
        <button class="icon-btn" @click="shiftMonth(-1)" aria-label="Mois précédent">‹</button>
        <MonthPickerButton v-model="selectedMonth" @change="fetchPlanning" />
        <button class="icon-btn" @click="shiftMonth(1)" aria-label="Mois suivant">›</button>
      </div>
    </div>

    <div class="summary-grid">
      <div class="summary-card">
        <span>Employés</span>
        <strong>{{ filteredStaff.length }}</strong>
      </div>
      <div class="summary-card">
        <span>Pointés</span>
        <strong>{{ totalSummary.worked }}</strong>
      </div>
      <div class="summary-card">
        <span>Absences</span>
        <strong>{{ totalSummary.absence }}</strong>
      </div>
      <div class="summary-card">
        <span>Arrêts</span>
        <strong>{{ totalSummary.arret }}</strong>
      </div>
    </div>

    <div class="filter-bar">
      <input v-model="searchQuery" type="search" placeholder="Rechercher un employé, poste..." />
      <select v-model="statusFilter">
        <option value="">Tous les statuts</option>
        <option value="worked">Pointés</option>
        <option value="absence">Absences</option>
        <option value="repos">Repos</option>
        <option value="arret">Arrêts</option>
        <option value="conge">Congés / maladie</option>
        <option value="conflit">Conflits</option>
      </select>
    </div>

    <div v-if="loading" class="state-card">Chargement du planning...</div>
    <div v-else-if="!filteredStaff.length" class="state-card">Aucun employé pour cette période</div>

    <div v-else class="planning-table-wrap">
      <table class="planning-table">
        <thead>
          <tr>
            <th class="staff-col">Employé</th>
            <th v-for="day in monthDays" :key="day.date" :class="{ sunday: day.isSunday }">
              <span>{{ day.day }}</span>
              <small>{{ day.weekday }}</small>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="staff in filteredStaff" :key="staff.staff_id">
            <td class="staff-col">
              <button class="staff-link" @click="openStaffDossier(staff.staff_id)">
                <strong>{{ staff.first_name }} {{ staff.last_name }}</strong>
                <span>{{ staff.post_name }}</span>
              </button>
              <div class="row-summary">
                <span>{{ staff.summary.worked || 0 }} pointés</span>
                <span>{{ staff.summary.absence || 0 }} abs.</span>
                <span>{{ staff.summary.repos || 0 }} repos</span>
              </div>
            </td>
            <td v-for="day in staff.days" :key="day.date">
              <button
                class="day-cell"
                :class="day.status"
                :title="dayTitle(day)"
                @click="openDay(staff, day)"
              >
                <span>{{ shortLabel(day) }}</span>
                <small v-if="day.hours">{{ Number(day.hours).toFixed(1) }}h</small>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="legend">
      <span v-for="item in legend" :key="item.status"><i :class="item.status"></i>{{ item.label }}</span>
    </div>

    <div v-if="selectedDay" class="sheet-overlay" @click.self="closeSheet">
      <div class="day-sheet">
        <div class="sheet-head">
          <div>
            <p class="eyebrow">Dossier journée</p>
            <h2>{{ selectedStaff.first_name }} {{ selectedStaff.last_name }}</h2>
            <div class="sheet-meta">
              <span>{{ selectedStaff.post_name }}</span>
              <span>{{ formatDate(selectedDay.date) }}</span>
              <strong :class="['status-pill', selectedDay.status]">{{ selectedDay.label || shortLabel(selectedDay) }}</strong>
            </div>
          </div>
          <button class="icon-btn" @click="closeSheet">×</button>
        </div>

        <textarea v-model="dayMotif" class="motif-input" placeholder="Motif, justification ou note RH"></textarea>

        <div class="action-sections">
          <section v-for="group in sheetActionGroups" :key="group.title" class="action-section">
            <p>{{ group.title }}</p>
            <div class="action-grid">
              <button
                v-for="option in group.options"
                :key="option.value"
                :class="['action-choice', option.value]"
                @click="saveDay(option.value)"
              >
                <span>{{ option.label }}</span>
                <small>{{ option.help }}</small>
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MonthPickerButton from './MonthPickerButton.vue';

const weekdayLabels = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

export default {
  name: 'AppPlanning',
  components: { MonthPickerButton },
  data() {
    return {
      selectedMonth: '',
      searchQuery: '',
      statusFilter: '',
      planning: [],
      loading: false,
      selectedStaff: null,
      selectedDay: null,
      dayMotif: '',
      statusOptions: {
        leave: [
          { value: 'repos', label: 'Repos accordé', help: 'Jour non travaillé' },
          { value: 'conge_paye', label: 'Congé payé', help: 'Payé 1 jour' },
          { value: 'conge_non_paye', label: 'Congé non payé', help: 'Non payé' },
          { value: 'maladie', label: 'Maladie', help: 'Justifié RH' },
        ],
        absence: [
          { value: 'absence_justifiee', label: 'Absence justifiée', help: 'Absence suivie' },
          { value: 'absence_non_justifiee', label: 'Absence non justifiée', help: 'Absence à traiter' },
        ],
      },
      legend: [
        { status: 'travail_effectue', label: 'Pointé' },
        { status: 'travail_prevu', label: 'Prévu' },
        { status: 'repos', label: 'Repos' },
        { status: 'conge_paye', label: 'Congé' },
        { status: 'absence_non_justifiee', label: 'Absence' },
        { status: 'arret', label: 'Arrêt' },
        { status: 'conflit_pointage', label: 'Conflit' },
      ],
    };
  },
  computed: {
    monthDays() {
      if (!this.planning.length) return [];
      return this.planning[0].days.map((day) => {
        const date = new Date(`${day.date}T00:00:00`);
        return {
          date: day.date,
          day: day.day,
          weekday: weekdayLabels[date.getDay()],
          isSunday: date.getDay() === 0,
        };
      });
    },
    filteredStaff() {
      const q = this.searchQuery.trim().toLowerCase();
      return this.planning.filter((staff) => {
        const matchesText = !q || `${staff.first_name} ${staff.last_name} ${staff.post_name}`.toLowerCase().includes(q);
        const matchesStatus = !this.statusFilter || this.staffMatchesStatus(staff, this.statusFilter);
        return matchesText && matchesStatus;
      });
    },
    totalSummary() {
      return this.filteredStaff.reduce((acc, staff) => {
        Object.keys(acc).forEach((key) => {
          acc[key] += Number(staff.summary?.[key] || 0);
        });
        return acc;
      }, { absence: 0, arret: 0, worked: 0, repos: 0, conge: 0, conflit: 0, prevu: 0 });
    },
    sheetActionGroups() {
      return [
        { title: 'Congés et repos', options: this.statusOptions.leave },
        { title: 'Absences', options: this.statusOptions.absence },
      ];
    },
  },
  mounted() {
    const now = new Date();
    this.selectedMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    this.fetchPlanning();
  },
  methods: {
    async fetchPlanning() {
      this.loading = true;
      try {
        const res = await fetch(`/api-pointage/planning-month?month=${this.selectedMonth}`, { cache: 'no-store' });
        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.error || 'Erreur chargement planning');
        }
        const data = await res.json();
        this.planning = Array.isArray(data) ? data : [];
      } catch (err) {
        alert(err.message);
        this.planning = [];
      } finally {
        this.loading = false;
      }
    },
    shiftMonth(delta) {
      const [year, month] = this.selectedMonth.split('-').map(Number);
      const date = new Date(year, month - 1 + delta, 1);
      this.selectedMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      this.fetchPlanning();
    },
    staffMatchesStatus(staff, status) {
      if (status === 'absence') return Number(staff.summary?.absence || 0) > 0;
      if (status === 'conge') return Number(staff.summary?.conge || 0) > 0;
      if (status === 'worked') return Number(staff.summary?.worked || 0) > 0;
      if (status === 'repos') return Number(staff.summary?.repos || 0) > 0;
      if (status === 'conflit') return Number(staff.summary?.conflit || 0) > 0;
      return Number(staff.summary?.[status] || 0) > 0;
    },
    openDay(staff, day) {
      if (day.status === 'hors_contrat') return;
      this.selectedStaff = staff;
      this.selectedDay = day;
      this.dayMotif = day.motif || '';
    },
    closeSheet() {
      this.selectedStaff = null;
      this.selectedDay = null;
      this.dayMotif = '';
    },
    async saveDay(status) {
      try {
        const res = await fetch('/api-pointage/planning-day', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            staff_id: this.selectedStaff.staff_id,
            date: this.selectedDay.date,
            status,
            motif: this.dayMotif,
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.error || 'Erreur mise à jour planning');
        }
        this.closeSheet();
        await this.fetchPlanning();
      } catch (err) {
        alert(err.message);
      }
    },
    openStaffDossier(staffId) {
      this.$router.push({ path: '/dossiers', query: { id: staffId } });
    },
    shortLabel(day) {
      const labels = {
        travail_effectue: 'P',
        repos_travaille: 'RT',
        repos: 'R',
        travail_prevu: 'Pr',
        conge_paye: 'CP',
        conge_non_paye: 'CNP',
        maladie: 'M',
        absence_justifiee: 'AJ',
        absence_non_justifiee: 'AN',
        arret: 'AR',
        conflit_pointage: '!',
      };
      return labels[day.status] || day.label || '';
    },
    dayTitle(day) {
      const parts = [this.formatDate(day.date), day.label || this.shortLabel(day)];
      if (day.hours) parts.push(`${Number(day.hours).toFixed(2)} h`);
      if (day.motif) parts.push(day.motif);
      return parts.filter(Boolean).join(' - ');
    },
    formatDate(value) {
      const [year, month, day] = value.split('-');
      return `${day}/${month}/${year}`;
    },
  },
};
</script>

<style scoped>
.planning-page {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.planning-toolbar,
.filter-bar,
.summary-grid,
.legend {
  max-width: 100%;
}

.planning-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
}

.eyebrow {
  margin: 0 0 4px;
  color: var(--primary);
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.planning-toolbar h1 {
  margin: 0;
  font-size: 24px;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon-btn {
  width: 40px;
  height: 40px;
  border: 1px solid var(--border);
  background: var(--white);
  border-radius: 8px;
  color: var(--gray-700);
  font-size: 22px;
  cursor: pointer;
}

.filter-bar input,
.filter-bar select,
.motif-input {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--white);
  color: var(--gray-800);
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.summary-card,
.state-card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
}

.summary-card {
  padding: 12px 15px;
}

.summary-card span {
  display: block;
  color: var(--gray-500);
  font-size: 12px;
  font-weight: 700;
}

.summary-card strong {
  display: block;
  margin-top: 2px;
  font-size: 22px;
}

.filter-bar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px;
  gap: 10px;
}

.filter-bar input,
.filter-bar select {
  min-height: 40px;
  padding: 0 14px;
}

.state-card {
  padding: 18px;
  text-align: center;
  color: var(--gray-500);
}

.planning-table-wrap {
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--white);
  box-shadow: var(--shadow-sm);
}

.planning-table {
  width: 100%;
  min-width: 1120px;
  border-collapse: collapse;
}

.planning-table th,
.planning-table td {
  border-bottom: 1px solid var(--border);
  border-right: 1px solid var(--border);
  padding: 5px;
  text-align: center;
}

.planning-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8fafc;
  color: var(--gray-600);
  font-size: 12px;
}

.planning-table th small {
  display: block;
  margin-top: 2px;
  color: var(--gray-400);
}

.planning-table th.sunday {
  background: #eef4ff;
}

.staff-col {
  position: sticky;
  left: 0;
  z-index: 3;
  width: 216px;
  min-width: 216px;
  background: var(--white);
  text-align: left !important;
}

.staff-link {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border: 0;
  background: transparent;
  color: var(--gray-900);
  cursor: pointer;
  text-align: left;
}

.staff-link span,
.row-summary {
  color: var(--gray-500);
  font-size: 12px;
}

.row-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.day-cell {
  width: 32px;
  min-height: 38px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  border: 1px solid var(--border);
  border-radius: 7px;
  background: #f8fafc;
  color: var(--gray-600);
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
}

.day-cell small {
  font-size: 9px;
}

.day-cell.travail_effectue { background: #dcfce7; border-color: #86efac; color: #166534; }
.day-cell.repos { background: #dbeafe; border-color: #93c5fd; color: #1d4ed8; }
.day-cell.repos_travaille { background: #bbf7d0; border-color: #22c55e; color: #14532d; }
.day-cell.travail_prevu { background: #f1f5f9; border-color: #cbd5e1; color: #475569; }
.day-cell.conge_paye,
.day-cell.conge_non_paye,
.day-cell.maladie { background: #ccfbf1; border-color: #5eead4; color: #0f766e; }
.day-cell.absence_justifiee { background: #ffedd5; border-color: #fdba74; color: #c2410c; }
.day-cell.absence_non_justifiee { background: #fee2e2; border-color: #fca5a5; color: #b91c1c; }
.day-cell.arret { background: #e5e7eb; border-color: #9ca3af; color: #374151; }
.day-cell.conflit_pointage { background: #fef2f2; border-color: #ef4444; color: #991b1b; }
.day-cell.hors_contrat { opacity: 0.35; cursor: default; }

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: var(--gray-600);
  font-size: 12px;
  font-weight: 700;
}

.legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.legend i {
  width: 12px;
  height: 12px;
  border-radius: 4px;
  border: 1px solid var(--border);
}

.legend i.travail_effectue { background: #dcfce7; }
.legend i.travail_prevu { background: #f1f5f9; }
.legend i.repos { background: #dbeafe; }
.legend i.conge_paye { background: #ccfbf1; }
.legend i.absence_non_justifiee { background: #fee2e2; }
.legend i.arret { background: #e5e7eb; }
.legend i.conflit_pointage { background: #fef2f2; }

.sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(15, 23, 42, 0.42);
  backdrop-filter: blur(3px);
}

.day-sheet {
  width: min(680px, 100%);
  max-height: calc(100vh - 36px);
  overflow-y: auto;
  background: var(--white);
  border-radius: 12px;
  box-shadow: var(--shadow-xl);
  padding: 18px;
}

.sheet-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.sheet-head h2 {
  margin: 0;
  font-size: 20px;
}

.sheet-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.sheet-meta span {
  color: var(--gray-500);
  font-size: 13px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 3px 10px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--gray-100);
  color: var(--gray-700);
  font-size: 12px;
  font-weight: 800;
}

.status-pill.travail_effectue,
.status-pill.repos_travaille { background: #dcfce7; border-color: #86efac; color: #166534; }
.status-pill.repos { background: #eff6ff; border-color: #bfdbfe; color: #1d4ed8; }
.status-pill.conge_paye,
.status-pill.conge_non_paye,
.status-pill.maladie { background: #f0fdfa; border-color: #99f6e4; color: #0f766e; }
.status-pill.absence_justifiee { background: #fff7ed; border-color: #fed7aa; color: #c2410c; }
.status-pill.absence_non_justifiee { background: #fef2f2; border-color: #fecaca; color: #b91c1c; }
.status-pill.arret { background: #f3f4f6; border-color: #d1d5db; color: #374151; }
.status-pill.conflit_pointage { background: #fef2f2; border-color: #ef4444; color: #991b1b; }

.action-sections {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 14px;
}

.action-section p {
  margin: 0 0 8px;
  color: var(--gray-500);
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.motif-input {
  width: 100%;
  min-height: 82px;
  margin-top: 14px;
  padding: 12px;
  resize: vertical;
}

.action-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.action-choice {
  min-height: 58px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--white);
  color: var(--gray-800);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 3px;
  padding: 10px 12px;
  text-align: left;
  font-weight: 800;
  cursor: pointer;
  border-left-width: 4px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.action-choice span {
  font-size: 13px;
}

.action-choice small {
  color: var(--gray-500);
  font-size: 11px;
  font-weight: 700;
}

.action-choice:hover {
  border-color: #cbd5e1;
  box-shadow: var(--shadow-sm);
  transform: translateY(-1px);
}

.action-choice.travail { border-left-color: #64748b; }
.action-choice.repos { border-left-color: #2563eb; }
.action-choice.conge_paye,
.action-choice.conge_non_paye,
.action-choice.maladie { border-left-color: #0d9488; }
.action-choice.absence_justifiee { border-left-color: #f97316; }
.action-choice.absence_non_justifiee { border-left-color: #dc2626; }

@media (max-width: 720px) {
  .planning-toolbar,
  .toolbar-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .summary-grid,
  .filter-bar {
    grid-template-columns: 1fr 1fr;
  }

  .filter-bar input {
    grid-column: 1 / -1;
  }

  .planning-table {
    min-width: 980px;
  }

  .staff-col {
    width: 190px;
    min-width: 190px;
  }

  .action-grid {
    grid-template-columns: 1fr;
  }

  .sheet-overlay {
    align-items: flex-end;
    padding: 10px;
  }

  .day-sheet {
    max-height: calc(100vh - 20px);
    border-radius: 12px 12px 0 0;
  }
}
</style>
